import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Bell, ListPlus, ChevronDown, ChevronRight, Pencil, Building2, Home as HomeIcon } from "lucide-react";
import http from "@/lib/http";
import { useAuth } from "@/lib/auth";
import ConfirmDialog from "@/components/ConfirmDialog";

const COLORS = [
  { k: "neutral", label: "Neutraal", chip: "" },
  { k: "teal",    label: "Teal",     chip: "chip-teal" },
  { k: "warn",    label: "Oranje",   chip: "chip-warn" },
  { k: "amber",   label: "Amber",    chip: "chip-amber" },
  { k: "yellow",  label: "Geel",     chip: "chip-yellow" },
  { k: "lime",    label: "Lime",     chip: "chip-lime" },
  { k: "success", label: "Groen",    chip: "chip-success" },
  { k: "blue",    label: "Blauw",    chip: "chip-blue" },
  { k: "indigo",  label: "Indigo",   chip: "chip-indigo" },
  { k: "purple",  label: "Paars",    chip: "chip-purple" },
  { k: "pink",    label: "Roze",     chip: "chip-pink" },
  { k: "rose",    label: "Rozerood", chip: "chip-rose" },
  { k: "danger",  label: "Rood",     chip: "chip-danger" },
  { k: "slate",   label: "Leigrijs", chip: "chip-slate" },
];

const SOURCE_TYPES = [
  { k: "makelaar", label: "Makelaars" },
  { k: "particulier", label: "Particulieren" },
];

const TARGET_VIEWS = [
  { k: "leads_new",                label: "Leads (Nieuw)" },
  { k: "parkeren",                 label: "Parkeren" },
  { k: "parkeren_zoeken",          label: "Zoeken" },
  { k: "parkeren_shortlist",       label: "Shortlist" },
  { k: "parkeren_signed",          label: "Getekend" },
  { k: "parkeren_lost",            label: "Verloren" },
  { k: "parkeren_ex_particulier",  label: "Ex-particulier" },
  { k: "hidden",                   label: "Niet zichtbaar (verborgen)" },
];

const TOP_TABS = [
  { k: "statuses", label: "Statussen" },
  { k: "actions", label: "Lead acties" },
];

export default function Statuses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "tenant_admin";
  const [topTab, setTopTab] = useState("statuses");
  const [tab, setTab] = useState("makelaar");
  const [statuses, setStatuses] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showNewAction, setShowNewAction] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [expandedRules, setExpandedRules] = useState({});
  // Custom confirm dialog state — replaces native window.confirm() which is
  // blocked inside iframes (e.g. the Emergent preview).
  const [confirmState, setConfirmState] = useState(null);
  const askConfirm = ({ title, message, confirmLabel = "Verwijderen", danger = true }) =>
    new Promise((resolve) => {
      setConfirmState({ title, message, confirmLabel, danger, resolve });
    });
  const closeConfirm = (result) => {
    if (confirmState?.resolve) confirmState.resolve(result);
    setConfirmState(null);
  };
  const [errorMsg, setErrorMsg] = useState(null);

  const refresh = async () => {
    setLoading(true);
    if (topTab === "statuses") {
      const r = await http.get("/api/lead-statuses", { params: { source_type: tab } });
      setStatuses(r.data);
    } else {
      const r = await http.get("/api/lead-actions");
      setActions(r.data);
    }
    setLoading(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab, topTab]);

  if (!isAdmin) {
    return (
      <div className="flex-1 p-8" data-testid="statuses-forbidden">
        <h1 className="font-display text-[28px]">Statussen</h1>
        <p className="text-[var(--ink-500)] mt-2">Alleen toegankelijk voor admins.</p>
      </div>
    );
  }

  const toggleRules = (id) => setExpandedRules(s => ({ ...s, [id]: !s[id] }));

  const saveStatus = async (form) => {
    if (editing) {
      await http.put(`/api/lead-statuses/${editing.id}`, {
        label: form.label, color: form.color, order: form.order,
        is_terminal: form.is_terminal, target_view: form.target_view,
      });
    } else {
      await http.post("/api/lead-statuses", {
        source_type: tab, key: form.key, label: form.label,
        color: form.color, order: form.order, is_terminal: form.is_terminal,
        target_view: form.target_view,
      });
    }
    setShowNew(false);
    setEditing(null);
    refresh();
  };

  const deleteStatus = async (s) => {
    const ok = await askConfirm({
      title: "Status verwijderen",
      message: `Weet je zeker dat je de status "${s.label}" wilt verwijderen? Alle regels op deze status worden ook verwijderd.`,
      confirmLabel: "Verwijderen",
      danger: true,
    });
    if (!ok) return;
    try {
      await http.delete(`/api/lead-statuses/${s.id}`);
      refresh();
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Kon niet verwijderen");
    }
  };

  const saveAction = async (form) => {
    const payload = {
      label: form.label, applies_to: form.applies_to,
      followup_days: Number(form.followup_days || 0),
      followup_days_makelaar: Number(form.followup_days_makelaar || 0),
      followup_days_particulier: Number(form.followup_days_particulier || 0),
      followup_task_title: form.followup_task_title,
      color: form.color, order: form.order, enabled: form.enabled,
    };
    if (editingAction) {
      await http.put(`/api/lead-actions/${editingAction.id}`, payload);
    } else {
      await http.post("/api/lead-actions", { ...payload, key: form.key });
    }
    setShowNewAction(false); setEditingAction(null); refresh();
  };
  const deleteAction = async (a) => {
    const ok = await askConfirm({
      title: "Actie verwijderen",
      message: `Weet je zeker dat je de actie "${a.label}" wilt verwijderen?`,
      confirmLabel: "Verwijderen",
      danger: true,
    });
    if (!ok) return;
    try {
      await http.delete(`/api/lead-actions/${a.id}`);
      refresh();
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Kon niet verwijderen");
    }
  };

  return (
    <div className="flex-1 p-8 overflow-auto" data-testid="statuses-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            Configuratie
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            Statussen & acties
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1 max-w-2xl">
            Definieer per pipeline eigen statussen + regels, en configureer de Brief-acties
            (Brief 1, Brief 2, …) die op de detail-pagina van leads beschikbaar zijn.
          </p>
        </div>
        {topTab === "statuses" ? (
          <button type="button" data-testid="btn-new-status" className="btn btn-primary"
                  onClick={() => { setShowNew(true); setEditing(null); }}>
            <Plus size={16} /> Nieuwe status
          </button>
        ) : (
          <button type="button" data-testid="btn-new-action" className="btn btn-primary"
                  onClick={() => { setShowNewAction(true); setEditingAction(null); }}>
            <Plus size={16} /> Nieuwe actie
          </button>
        )}
      </div>

      {/* Top tabs: Statussen vs Lead acties */}
      <div className="flex gap-1 border-b border-[var(--border-soft)] mb-5" data-testid="status-top-tabs">
        {TOP_TABS.map(s => (
          <button key={s.k} type="button" data-testid={`top-tab-${s.k}`}
                  onClick={() => setTopTab(s.k)}
                  className={`px-4 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px
                    ${topTab === s.k ? "text-[var(--teal-700)] border-[var(--teal-600)]"
                                     : "text-[var(--ink-500)] border-transparent hover:text-[var(--ink-900)]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {topTab === "statuses" && (
        <>
          {/* Source-type sub-tabs */}
          <div className="flex gap-1 mb-6" data-testid="status-source-tabs">
            {SOURCE_TYPES.map(s => (
              <button key={s.k} type="button" data-testid={`source-tab-${s.k}`}
                      onClick={() => setTab(s.k)}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors
                        ${tab === s.k ? "bg-[var(--teal-100)] text-[var(--teal-800)]"
                                      : "text-[var(--ink-500)] hover:text-[var(--ink-900)]"}`}>
                {s.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-[var(--ink-500)] text-sm">Laden…</div>
          ) : (
            <div className="space-y-3" data-testid="statuses-list">
              {statuses.map(s => (
                <StatusCard
                  key={s.id} status={s}
                  expanded={!!expandedRules[s.id]}
                  onToggle={() => toggleRules(s.id)}
                  onEdit={() => { setEditing(s); setShowNew(true); }}
                  onDelete={() => deleteStatus(s)}
                  onRulesChanged={refresh}
                />
              ))}
            </div>
          )}
        </>
      )}

      {topTab === "actions" && (
        <>
          {loading ? (
            <div className="text-[var(--ink-500)] text-sm">Laden…</div>
          ) : (
            <div className="space-y-3" data-testid="actions-list">
              {actions.map(a => (
                <ActionCard
                  key={a.id} action={a}
                  onEdit={() => { setEditingAction(a); setShowNewAction(true); }}
                  onDelete={() => deleteAction(a)}
                  onToggle={async () => {
                    await http.put(`/api/lead-actions/${a.id}`, { enabled: !a.enabled });
                    refresh();
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {showNew && (
        <StatusDialog
          initial={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSave={saveStatus}
        />
      )}
      {showNewAction && (
        <ActionDialog
          initial={editingAction}
          onClose={() => { setShowNewAction(false); setEditingAction(null); }}
          onSave={saveAction}
        />
      )}
      {confirmState && (
        <ConfirmDialog
          open={true}
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          onConfirm={() => closeConfirm(true)}
          onCancel={() => closeConfirm(false)}
        />
      )}
      {errorMsg && (
        <ErrorToast message={errorMsg} onClose={() => setErrorMsg(null)} />
      )}
    </div>
  );
}

function ErrorToast({ message, onClose }) {
  useEffect(() => {
    const id = setTimeout(onClose, 5000);
    return () => clearTimeout(id);
  }, [onClose]);
  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm bg-[#FBE7E7] border border-[#EFBABA] text-[#7B2424] px-4 py-3 rounded-lg shadow-lg flex items-start gap-3"
         data-testid="error-toast">
      <X size={16} className="mt-0.5 cursor-pointer text-[#7B2424]" onClick={onClose} />
      <div className="text-[13px]">{message}</div>
    </div>
  );
}

function StatusCard({ status, expanded, onToggle, onEdit, onDelete, onRulesChanged }) {
  const colorChipClass = COLORS.find(c => c.k === status.color)?.chip || "";
  return (
    <div className="card !p-0 overflow-hidden" data-testid={`status-card-${status.id}`}>
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button type="button" onClick={onToggle}
                  data-testid={`status-toggle-${status.id}`}
                  className="text-[var(--ink-400)] hover:text-[var(--ink-700)]">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <span className={`chip ${colorChipClass} font-medium`}>{status.label}</span>
          <code className="text-[11px] text-[var(--ink-400)] tabular">{status.key}</code>
          <span className="text-[11px] text-[var(--ink-500)] inline-flex items-center gap-1">
            →
            {TARGET_VIEWS.find(t => t.k === status.target_view)?.label || status.target_view || "—"}
          </span>
          {status.is_terminal && (
            <span className="text-[11px] text-[var(--ink-400)] uppercase tracking-wider">terminal</span>
          )}
          <span className="text-[12px] text-[var(--ink-500)]">
            {status.rules?.length || 0} regel(s)
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onEdit} data-testid={`status-edit-${status.id}`}
                  className="p-2 rounded-md hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--ink-900)]" title="Bewerken">
            <Pencil size={15} />
          </button>
          <button type="button" onClick={onDelete} data-testid={`status-delete-${status.id}`}
                  className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]"
                  title="Status verwijderen">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[var(--border-soft)] bg-[var(--sand-50)] p-4">
          <RulesPanel status={status} onChanged={onRulesChanged} />
        </div>
      )}
    </div>
  );
}

function RulesPanel({ status, onChanged }) {
  const [adding, setAdding] = useState(null); // null | "create_task" | "remind_after_days"
  const [form, setForm] = useState({});
  const startAdd = (type) => {
    setAdding(type);
    setForm(type === "create_task"
      ? { task_title: "", due_offset_days: 0 }
      : { remind_days: 30, remind_message: "" });
  };
  const submit = async () => {
    const payload = { type: adding, enabled: true, ...form };
    if (adding === "create_task") {
      payload.due_offset_days = Number(form.due_offset_days || 0);
    } else {
      payload.remind_days = Number(form.remind_days || 0);
    }
    await http.post(`/api/lead-statuses/${status.id}/rules`, payload);
    setAdding(null);
    onChanged();
  };
  const toggleEnabled = async (rule) => {
    await http.put(`/api/lead-statuses/${status.id}/rules/${rule.id}`, { enabled: !rule.enabled });
    onChanged();
  };
  const deleteRule = async (rule) => {
    if (!window.confirm("Regel verwijderen?")) return;
    await http.delete(`/api/lead-statuses/${status.id}/rules/${rule.id}`);
    onChanged();
  };

  return (
    <div className="space-y-3">
      {(status.rules || []).length === 0 && !adding && (
        <div className="text-[12px] text-[var(--ink-500)] italic">
          Geen regels. Voeg er één toe hieronder.
        </div>
      )}
      {(status.rules || []).map(r => (
        <div key={r.id} data-testid={`rule-row-${r.id}`}
             className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[var(--border-soft)]">
          {r.type === "create_task" ? (
            <ListPlus size={16} className="text-[var(--teal-600)] mt-0.5" />
          ) : (
            <Bell size={16} className="text-[#8A5D10] mt-0.5" />
          )}
          <div className="flex-1 text-[13px]">
            {r.type === "create_task" ? (
              <>
                <div className="font-medium">Auto-taak: "{r.task_title}"</div>
                <div className="text-[11px] text-[var(--ink-500)] mt-0.5">
                  Wordt aangemaakt zodra een lead deze status krijgt
                  {r.due_offset_days != null && ` · deadline +${r.due_offset_days} dagen`}.
                </div>
              </>
            ) : (
              <>
                <div className="font-medium">
                  Herinnering na {r.remind_days} dagen
                </div>
                <div className="text-[11px] text-[var(--ink-500)] mt-0.5">
                  {r.remind_message || "(geen bericht)"}
                </div>
              </>
            )}
          </div>
          <label className="text-[12px] text-[var(--ink-500)] flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={r.enabled}
                   data-testid={`rule-enabled-${r.id}`}
                   onChange={() => toggleEnabled(r)}
                   className="accent-[var(--teal-600)]" />
            actief
          </label>
          <button type="button" onClick={() => deleteRule(r)}
                  data-testid={`rule-delete-${r.id}`}
                  className="p-1.5 rounded-md hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--danger)]">
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      {adding && (
        <div className="p-3 bg-white rounded-lg border border-[var(--teal-300,#86c9c2)] space-y-3"
             data-testid="rule-form">
          {adding === "create_task" ? (
            <>
              <Input label="Taaktitel" value={form.task_title}
                     testid="rule-task-title"
                     onChange={(v) => setForm({ ...form, task_title: v })}
                     placeholder="Bv. Bel terug na Brief 1" />
              <Input label="Deadline (dagen vanaf nu)" type="number" value={form.due_offset_days}
                     testid="rule-due-offset"
                     onChange={(v) => setForm({ ...form, due_offset_days: v })} />
            </>
          ) : (
            <>
              <Input label="Aantal dagen" type="number" value={form.remind_days}
                     testid="rule-remind-days"
                     onChange={(v) => setForm({ ...form, remind_days: v })} />
              <Input label="Bericht (optioneel)" value={form.remind_message}
                     testid="rule-remind-msg"
                     onChange={(v) => setForm({ ...form, remind_message: v })}
                     placeholder="Bv. Brief 1 staat 30 dagen — overweeg Brief 2" />
            </>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setAdding(null)}
                    className="btn btn-ghost !h-9 !px-3 !text-[12px]">
              <X size={13} /> Annuleren
            </button>
            <button type="button" data-testid="btn-save-rule" onClick={submit}
                    className="btn btn-primary !h-9 !px-3 !text-[12px]">
              <Save size={13} /> Opslaan
            </button>
          </div>
        </div>
      )}

      {!adding && (
        <div className="flex gap-2">
          <button type="button" data-testid="btn-add-task-rule"
                  onClick={() => startAdd("create_task")}
                  className="btn btn-ghost !h-9 !px-3 !text-[12px]">
            <ListPlus size={13} /> Auto-taak toevoegen
          </button>
          <button type="button" data-testid="btn-add-remind-rule"
                  onClick={() => startAdd("remind_after_days")}
                  className="btn btn-ghost !h-9 !px-3 !text-[12px]">
            <Bell size={13} /> Herinnering toevoegen
          </button>
        </div>
      )}
    </div>
  );
}

function StatusDialog({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    key: initial?.key || "",
    label: initial?.label || "",
    color: initial?.color || "neutral",
    order: initial?.order ?? 99,
    is_terminal: initial?.is_terminal || false,
    target_view: initial?.target_view || "leads_new",
  });
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try { await onSave(form); }
    catch (e2) { setErr(e2?.response?.data?.detail || "Opslaan mislukt"); }
  };
  return (
    <div className="fixed inset-0 bg-black/30 z-40 grid place-items-center p-4" data-testid="status-dialog">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="font-display text-[22px]">{initial ? "Status bewerken" : "Nieuwe status"}</h2>
        {!initial && (
          <Input label="Sleutel (intern, kleine letters / underscore)"
                 value={form.key} testid="status-key"
                 onChange={(v) => setForm({ ...form, key: v.toLowerCase().replace(/\s+/g, "_") })}
                 placeholder="bv. brief_1" />
        )}
        <Input label="Label (zichtbaar)" value={form.label} testid="status-label"
               onChange={(v) => setForm({ ...form, label: v })}
               placeholder="bv. Brief 1 verstuurd" />
        <label className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Kleur</div>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button key={c.k} type="button"
                      data-testid={`color-${c.k}`}
                      onClick={() => setForm({ ...form, color: c.k })}
                      className={`chip ${c.chip} ${form.color === c.k ? "ring-2 ring-[var(--teal-500)]" : ""}`}>
                {c.label}
              </button>
            ))}
          </div>
        </label>
        <Input label="Volgorde" type="number" value={form.order} testid="status-order"
               onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
        <label className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">
            Verschijnt op pagina (zodra een lead deze status krijgt)
          </div>
          <select value={form.target_view}
                  data-testid="status-target-view"
                  onChange={(e) => setForm({ ...form, target_view: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm bg-white">
            {TARGET_VIEWS.map(t => (
              <option key={t.k} value={t.k}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={form.is_terminal} data-testid="status-terminal"
                 onChange={(e) => setForm({ ...form, is_terminal: e.target.checked })}
                 className="accent-[var(--teal-600)]" />
          Terminal-status (geen verdere regels worden uitgevoerd)
        </label>
        {err && <div className="text-[12px] text-[var(--danger)]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Annuleren</button>
          <button type="submit" data-testid="btn-save-status" className="btn btn-primary">
            <Save size={14} /> Opslaan
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">{label}</div>
      <input type={type} value={value ?? ""} placeholder={placeholder}
             data-testid={testid}
             onChange={(e) => onChange(e.target.value)}
             className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
    </label>
  );
}

function ActionCard({ action, onEdit, onDelete, onToggle }) {
  const colorChip = COLORS.find(c => c.k === action.color)?.chip || "";
  return (
    <div className="card !p-4 flex items-center justify-between gap-3"
         data-testid={`action-card-${action.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap mb-1">
          <span className={`chip ${colorChip} font-medium`}>{action.label}</span>
          <code className="text-[11px] text-[var(--ink-400)] tabular">{action.key}</code>
          {!action.enabled && (
            <span className="chip chip-danger !text-[10px] !py-[1px]">uitgeschakeld</span>
          )}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] flex flex-wrap gap-x-3 gap-y-1">
          <span>Voor: {(action.applies_to || []).join(" + ")}</span>
          <span className="text-[var(--ink-300)]">·</span>
          <span>Opvolg-taak: <strong>{action.followup_task_title || "(geen titel)"}</strong></span>
          <span className="text-[var(--ink-300)]">·</span>
          <span>
            Deadline:{" "}
            {(action.applies_to || []).includes("makelaar") && (
              <span title="Voor makelaar-panden">M +{action.followup_days_makelaar ?? action.followup_days}d</span>
            )}
            {(action.applies_to || []).includes("makelaar") && (action.applies_to || []).includes("particulier") && " · "}
            {(action.applies_to || []).includes("particulier") && (
              <span title="Voor particulier-panden">P +{action.followup_days_particulier ?? action.followup_days}d</span>
            )}
          </span>
        </div>
      </div>
      <label className="text-[12px] text-[var(--ink-500)] flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" checked={action.enabled}
               data-testid={`action-enabled-${action.id}`}
               onChange={onToggle}
               className="accent-[var(--teal-600)]" />
        actief
      </label>
      <button type="button" onClick={onEdit} data-testid={`action-edit-${action.id}`}
              className="p-2 rounded-md hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--ink-900)]" title="Bewerken">
        <Pencil size={15} />
      </button>
      <button type="button" onClick={onDelete} data-testid={`action-delete-${action.id}`}
              className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]"
              title="Actie verwijderen">
        <Trash2 size={15} />
      </button>
    </div>
  );
}

function ActionDialog({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    key: initial?.key || "",
    label: initial?.label || "",
    applies_to: initial?.applies_to || ["makelaar", "particulier"],
    followup_days: initial?.followup_days ?? 30,
    followup_days_makelaar: initial?.followup_days_makelaar ?? initial?.followup_days ?? 30,
    followup_days_particulier: initial?.followup_days_particulier ?? initial?.followup_days ?? 30,
    followup_task_title: initial?.followup_task_title || "",
    color: initial?.color || "neutral",
    order: initial?.order ?? 99,
    enabled: initial?.enabled !== false,
  });
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try { await onSave(form); }
    catch (e2) { setErr(e2?.response?.data?.detail || "Opslaan mislukt"); }
  };
  const toggleApply = (st) => {
    setForm(f => ({
      ...f,
      applies_to: f.applies_to.includes(st)
        ? f.applies_to.filter(x => x !== st)
        : [...f.applies_to, st],
    }));
  };
  return (
    <div className="fixed inset-0 bg-black/30 z-40 grid place-items-center p-4" data-testid="action-dialog">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="font-display text-[22px]">{initial ? "Actie bewerken" : "Nieuwe actie"}</h2>
        {!initial && (
          <Input label="Sleutel (intern)" value={form.key} testid="action-key"
                 onChange={(v) => setForm({ ...form, key: v.toLowerCase().replace(/\s+/g, "_") })}
                 placeholder="bv. brief_4" />
        )}
        <Input label="Label (zichtbaar op knop)" value={form.label} testid="action-label"
               onChange={(v) => setForm({ ...form, label: v })}
               placeholder="bv. Brief 4" />
        <label className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Beschikbaar voor</div>
          <div className="flex gap-3">
            {SOURCE_TYPES.map(s => (
              <label key={s.k} className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input type="checkbox" checked={form.applies_to.includes(s.k)}
                       data-testid={`action-applies-${s.k}`}
                       onChange={() => toggleApply(s.k)}
                       className="accent-[var(--teal-600)]" />
                {s.label}
              </label>
            ))}
          </div>
        </label>
        <Input label="Opvolg-taak titel" value={form.followup_task_title}
               testid="action-task-title"
               onChange={(v) => setForm({ ...form, followup_task_title: v })}
               placeholder="bv. Verstuur Brief 5 / Bel terug" />
        <div className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Opvolg-taak deadline (dagen)</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] text-[var(--ink-500)] mb-1 flex items-center gap-1">
                <Building2 size={11} /> Makelaars
              </div>
              <input type="number" value={form.followup_days_makelaar}
                     data-testid="action-followup-days-makelaar"
                     onChange={(e) => setForm({ ...form, followup_days_makelaar: Number(e.target.value) || 0 })}
                     disabled={!form.applies_to.includes("makelaar")}
                     className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm tabular focus:outline-none focus:border-[var(--teal-500)] disabled:bg-[var(--sand-100)] disabled:text-[var(--ink-400)]" />
            </label>
            <label className="block">
              <div className="text-[11px] text-[var(--ink-500)] mb-1 flex items-center gap-1">
                <HomeIcon size={11} /> Particulieren
              </div>
              <input type="number" value={form.followup_days_particulier}
                     data-testid="action-followup-days-particulier"
                     onChange={(e) => setForm({ ...form, followup_days_particulier: Number(e.target.value) || 0 })}
                     disabled={!form.applies_to.includes("particulier")}
                     className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm tabular focus:outline-none focus:border-[var(--teal-500)] disabled:bg-[var(--sand-100)] disabled:text-[var(--ink-400)]" />
            </label>
          </div>
          <div className="text-[11px] text-[var(--ink-500)] mt-1">
            Twee aparte termijnen: bij een makelaar-pand wordt het bovenste getal gebruikt, bij een particulier-pand het onderste.
          </div>
        </div>
        <label className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Kleur (knop wordt deze kleur na uitvoering)</div>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button key={c.k} type="button"
                      data-testid={`action-color-${c.k}`}
                      onClick={() => setForm({ ...form, color: c.k })}
                      className={`chip ${c.chip} ${form.color === c.k ? "ring-2 ring-[var(--teal-500)]" : ""}`}>
                {c.label}
              </button>
            ))}
          </div>
        </label>
        <Input label="Volgorde" type="number" value={form.order} testid="action-order"
               onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={form.enabled} data-testid="action-enabled-input"
                 onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                 className="accent-[var(--teal-600)]" />
          Actief
        </label>
        {err && <div className="text-[12px] text-[var(--danger)]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Annuleren</button>
          <button type="submit" data-testid="btn-save-action" className="btn btn-primary">
            <Save size={14} /> Opslaan
          </button>
        </div>
      </form>
    </div>
  );
}
